const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/

/**
 * Extract the ISO calendar-date part (YYYY-MM-DD) from an ISO date or datetime
 * string. Returns undefined when the value is empty or does not start with a
 * calendar date. Unlike `new Date(...).toISOString()`, this never shifts the
 * day across timezones because it operates purely on the string.
 */
export function isoDatePart(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  return ISO_DATE_PREFIX.exec(value)?.[0]
}

/**
 * Parse an ISO calendar date (YYYY-MM-DD) into a `Date` at LOCAL midnight.
 *
 * Native `new Date('YYYY-MM-DD')` interprets the string as UTC midnight, which
 * renders as the previous calendar day in timezones west of UTC. Building the
 * date from its parts keeps it on the intended day everywhere. Returns null
 * when the value cannot be parsed.
 */
export function parseIsoDateLocal(value: string | null | undefined): Date | null {
  if (!value) return null
  const match = ISO_DATE_PREFIX.exec(value)
  if (!match) {
    const fallback = new Date(value)
    return Number.isNaN(fallback.getTime()) ? null : fallback
  }
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Today's date in the browser's local timezone as an ISO calendar date
 * (YYYY-MM-DD). Prefer this over `new Date().toISOString().slice(0, 10)`, which
 * yields the UTC date and can be a day ahead in timezones west of UTC.
 */
export function todayIsoDate(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format an ISO calendar date (YYYY-MM-DD) as a human-readable US English date
 * (e.g. "August 15, 1996"), or an em dash when the value is missing/invalid.
 * Parses as a local date so the displayed day never rolls back a day in
 * timezones west of UTC.
 */
export function formatBirthdate(value: string | undefined): string {
  const d = parseIsoDateLocal(value)
  if (!d) return '—'
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}
